import type { QueryObserverBaseResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { DetailUser } from '@zelos/codegen/apis';
import { V1Service } from '@zelos/codegen/apis';
import {
  atom,
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'jotai';
import { useEffect, useMemo } from 'react';
import { createStoreService } from './default-store.service';
import { isServerSide } from './environment';
import { refreshSession } from './refresh-session.service';
import {
  resetAtomAccessCookie,
  useAccessCookie,
} from '@zelos/shared/lib/access-cookie';

const isSignedInAtom = atom<boolean | null>(null);
const detailUserAtom = atom<DetailUser | null>(null);
const refetchDetailUserAtom = atom<{ refetchDetailUser: QueryObserverBaseResult['refetch'] | null }>({ refetchDetailUser: null });

const {
  get: getIsSignedIn,
  set: setIsSignedIn,
} = createStoreService(isSignedInAtom);

export const useIsSignedIn = () => useAtomValue(isSignedInAtom);
export const useDetailUser = () => useAtomValue(detailUserAtom);
export const useRefetchDetailUser = () => useAtomValue(refetchDetailUserAtom).refetchDetailUser;

export const useUserHook = () => {
  const accessCookie = useAccessCookie();
  const [detailUser, setDetailUser] = useAtom(detailUserAtom);
  const setRefetchDetailUser = useSetAtom(refetchDetailUserAtom);

  const {
    data: isTokenVerified,
    isFetching: isTokenVerifyFetching,
  } = useQuery([
    'v1UsersTokenVerifyCreate',
    accessCookie?.valueOf(),
  ], async () => {
    if (isServerSide) return false;

    if (accessCookie?.isNeedRefresh()) {
      await refreshSession();
      return false;
    }

    if (!accessCookie?.valueOf()) {
      setIsSignedIn(false);
      setDetailUser(null);
      return false;
    }

    await V1Service.v1UsersTokenVerifyCreate();
    return true;
  }, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: detailUserData,
    refetch: refetchDetailUser,
    isFetched: isDetailUserFetched,
  } = useQuery([
    'v1UsersProfileRetrieve',
    accessCookie?.valueOf(),
  ], V1Service.v1UsersProfileRetrieve, {
    enabled: Boolean(isTokenVerified),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!isDetailUserFetched) return;

    setDetailUser(detailUserData || null);

    const hasDetailUser = Boolean(detailUserData);
    hasDetailUser !== getIsSignedIn()
    && setIsSignedIn(hasDetailUser);
  }, [
    detailUserData,
    isDetailUserFetched,
    setDetailUser,
  ]);

  useEffect(() => {
    getIsSignedIn() === null
    && detailUserData
    && detailUser === null
    && (setDetailUser(detailUserData), setIsSignedIn(true));
  }, [
    detailUserData,
    detailUser,
    setDetailUser,
  ]);

  useEffect(() => {
    isTokenVerified === undefined
    && !isTokenVerifyFetching
    && resetAtomAccessCookie();
  }, [
    isTokenVerified,
    isTokenVerifyFetching,
  ]);

  useEffect(() => {
    setRefetchDetailUser({ refetchDetailUser });
  }, [refetchDetailUser, setRefetchDetailUser]);
};

export const useIsOrganization = () => {
  const detailUser = useDetailUser();
  return useMemo(() => detailUser && !!detailUser.organization, [detailUser]);
};

export const signIn = Object.assign(async (
  email: string,
  password: string,
) => {
  if (signIn.inProgress || isServerSide) return;

  try {
    signIn.inProgress = true;

    setIsSignedIn(null);
    await V1Service.v1UsersLoginCreate({
      requestBody: { email, password },
    });
  } finally {
    resetAtomAccessCookie();
    signIn.inProgress = false;
  }
}, { inProgress: false });

export const signOut = Object.assign(async ({ clearAccessCookieAtom = false } = {}) => {
  if (signOut.inProgress || isServerSide) return;

  const clearAccessCookieForMobileSafari = () => document.cookie = 'access=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; Path=/';

  try {
    signOut.inProgress = true;

    await V1Service.v1UsersTokenLogoutCreate();
    clearAccessCookieForMobileSafari();
  } finally {
    clearAccessCookieAtom && resetAtomAccessCookie();
    signOut.inProgress = false;
  }
}, { inProgress: false });
